import React, { useEffect, useRef } from 'react';
import _isEmpty from 'lodash/isEmpty';
import { Transformation } from 'cloudinary-react';
import { constants, helpers } from '@minecraft.utils';
import { gqlMediaTypeCodeEnum as MEDIA_TYPE } from '@minecraft.graphql-types';
import { useFeatureExperiment } from '@blocs.features-experiments';
import { useTranslation } from '@blocs.i18n';

import CloudinaryVideoPlayer from './CloudinaryVideoPlayer';
import CloudinaryVideoThumbnail from './CloudinaryVideoThumbnail';
import { StyledCloudinaryVideo, StyledLinkWrapper } from './styles';
import { audioPlayerHeight, cloudinaryEnvironment, CloudinaryVideoAudioProps, sizes } from './common';

const { videoTransformationParams, audioTransformationParams } = constants;
const { isMediaInColdStorage: checkIsMediaInColdStorage, getMediaDownloadOriginalUrl } = helpers;

const CloudinaryVideoAudio: React.FC<CloudinaryVideoAudioProps> = ({
  'data-qa-id': qaId,
  'data-testid': testId,
  cloudName,
  controls = true,
  customSize = [],
  fileKey,
  format,
  guid,
  hidePlayIcon = false,
  isThumbnail,
  isVisible = true,
  mediaStatus,
  mediaStorageStatus,
  mediaType,
  preload = 'none',
  responsiveWidth = false,
  thumbnail: thumbnailObject,
  thumbnailOffset = 'auto',
  thumbnailUrl,
  type = 'md',
  url: primaryUrl,
  withDownloadLink = false,
  autoPlay = false,
  ...rest
}) => {
  const { t } = useTranslation();
  const videoAudioRef = useRef(null);
  // Based on type (xsm, sm, md, lg) we set the height & width of the video container
  const [width, height] = _isEmpty(customSize) ? sizes[type] : customSize;
  const isAudio = mediaType === MEDIA_TYPE.AUDIO;
  const isVideo = mediaType === MEDIA_TYPE.VIDEO;
  const mediaPlayerHeight = isAudio ? audioPlayerHeight : height;
  // Depending on these statuses, after Migration or after 90 days
  // Media assets will be transfered to CN-COLD and have one of the statuses
  // Listed in coldStorageMediaStatusList
  const isMediaInColdStorage = checkIsMediaInColdStorage(mediaStorageStatus);
  // Depending if the assets is in cn-cold (s3) or cn-hot, we should set the cloudName accordingly
  const cloudinaryCloudName = isMediaInColdStorage
    ? cloudinaryEnvironment().coldStorageCloudName
    : cloudinaryEnvironment().hotStorageCloudName;

  const thumbnailUrlToUse = thumbnailObject?.url || thumbnailUrl;
  const videoPoster = isMediaInColdStorage ? thumbnailUrlToUse : { transformation: { startOffset: thumbnailOffset } };

  // Setting Downloadble link for the asset
  const hasDownloadLink = withDownloadLink && Boolean(primaryUrl);
  const downloadUrl = getMediaDownloadOriginalUrl({ hasDownloadLink, primaryUrl, fileKey });

  // Preload for Audio (Player issue)
  const mediaPreload = isAudio ? 'metadata' : preload;

  useEffect(() => {
    if (!isVisible) {
      videoAudioRef?.current?.pause();
    }
  }, [isVisible, videoAudioRef]);
  const cloudinaryVideoPoster = isVideo ? videoPoster : '';
  const cloudinaryVideoThumbnail = isMediaInColdStorage ? thumbnailObject : null;
  const cloudinaryVideoThumbnailPoster = isMediaInColdStorage ? videoPoster : rest.poster;

  return isThumbnail && isVideo ? (
    <CloudinaryVideoThumbnail
      height={height}
      width={width}
      isMediaInColdStorage={isMediaInColdStorage}
      mediaStorageStatus={mediaStorageStatus}
      type={type}
      hidePlayIcon={hidePlayIcon}
      fileKey={fileKey}
      thumbnail={cloudinaryVideoThumbnail}
      data-qa-id={qaId}
      data-testid={testId}
      {...rest}
      poster={cloudinaryVideoThumbnailPoster}
    />
  ) : (
    <div data-testid={testId}>
      <StyledCloudinaryVideo
        innerRef={videoAudioRef}
        // Has to be here, defines where we shuold look for a file
        cloudName={cloudinaryCloudName}
        // If we should display play volume etc.
        controls={controls}
        // Defines want format should we fetch, for Audio it should be ogg
        // For video, we don't specify
        // Based on this property, it has different styles
        $isVideo={isVideo}
        // Defines what should be the poster of the video
        // For cn-hot it's goint to take "auto" frame
        // For cn-cold we need to specify an URL cause
        // we can't transform COLD videos
        poster={cloudinaryVideoPoster}
        // ID of a file within certain environment
        publicId={fileKey}
        // For Audio components to play
        resourceType="video"
        // Defines if we should preload the asset
        // For videos we shouldn't as the thumbnails for cn-cold are taken
        // From GUID (see CloudinaryVideoThumbnail)
        // And CN-HOT will take the certain frame
        preload={mediaPreload}
        // To remove three dots
        controlsList="nodownload"
        // Set to "" to make cn-cold videos playable
        sourceTypes=""
        // To omit transformations
        $wrapperHeight={mediaPlayerHeight}
        $wrapperWidth={width}
        // Rest of CloudinaryComponentProps
        {...rest}
        // DO NOT TOUCH, SHOULD NOT BE CHANGED
        secure
        secureDistribution={cloudinaryEnvironment().domain}
        // responsiveWidth for width 100%
        $responsiveWidth={responsiveWidth}
        autoPlay={autoPlay}
      >
        {isVideo && isMediaInColdStorage && (
          <Transformation
            // So we're only getting MP4's (required)
            fetchFormat={videoTransformationParams.f}
            // So we're only gettin h264 codec type (required)
            videoCodec={videoTransformationParams.vc}
            quality={videoTransformationParams.q}
            crop={videoTransformationParams.c}
            height={videoTransformationParams.h}
            width={videoTransformationParams.w}
          />
        )}
        {isAudio && (
          <Transformation
            fetchFormat={audioTransformationParams.f}
            audioCodec={audioTransformationParams.ac}
            quality={audioTransformationParams.q}
          />
        )}
      </StyledCloudinaryVideo>
      {hasDownloadLink && (
        <StyledLinkWrapper>
          <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
            {t('common:button.download')}
          </a>
        </StyledLinkWrapper>
      )}
    </div>
  );
};

const useNormalizedVideoTransformations = (media: Pick<CloudinaryVideoAudioProps, 'mediaType'>) => {
  const normalizeVideoTransformations = useFeatureExperiment('NORMALIZE_VIDEO_TRANSFORMATIONS');
  return normalizeVideoTransformations && media.mediaType === MEDIA_TYPE.VIDEO;
};
const CloudinaryVideoAudioWrapper: React.FC<CloudinaryVideoAudioProps> = (props) => {
  const normalizeVideoTransformations = useNormalizedVideoTransformations(props);
  if (normalizeVideoTransformations) {
    return <CloudinaryVideoPlayer {...props} size={props.type} />;
  }
  return <CloudinaryVideoAudio {...props} />;
};

export default CloudinaryVideoAudioWrapper;
